import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  MagnifyingGlass,
  HeadCircuit,
  Wrench,
  Leaf,
  Phone,
  ArrowRight,
  ShieldCheck,
  CurrencyEur,
  Sparkle,
  Certificate,
  ChatCircleDots,
  Clock,
  House,
  Star,
} from "@phosphor-icons/react";
import { COMPANY, IMAGES, ADVANTAGES, STEPS } from "../lib/constants";
import Testimonials from "../components/Testimonials";
import FAQ from "../components/FAQ";
import CallbackForm from "../components/CallbackForm";
import Realisations from "../components/Realisations";
import Aides from "../components/Aides";

const iconMap = {
  MagnifyingGlass,
  HeadCircuit,
  Wrench,
  Leaf,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative eco-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-1.5 rounded-full text-xs font-semibold mb-6">
                <Leaf size={14} weight="fill" /> Solutions énergétiques pour l'habitat
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight">
                Réduisez vos factures <br className="hidden md:block" />
                <span className="bg-gradient-to-r from-blue-700 to-emerald-600 bg-clip-text text-transparent">
                  de chauffage
                </span>{" "}
                dès maintenant
              </h1>
              <p className="text-base md:text-lg text-slate-600 mt-5 max-w-xl leading-relaxed">
                Vérifiez votre éligibilité aux solutions énergétiques adaptées à
                votre logement. Étude personnalisée et sans engagement,
                accompagnement complet.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/eligibilite"
                  data-testid="hero-eligibility-btn"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-semibold btn-primary-eco"
                >
                  Tester mon éligibilité gratuitement <ArrowRight size={18} weight="bold" />
                </Link>
                <a
                  href={COMPANY.phoneHref}
                  data-testid="hero-call-btn"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-semibold btn-secondary-eco"
                >
                  <Phone size={18} weight="fill" /> {COMPANY.phone}
                </a>
              </div>

              <p
                data-testid="hero-urgency"
                className="mt-3 text-sm text-slate-600 font-medium flex items-center gap-2"
              >
                <Clock size={14} weight="fill" className="text-emerald-600" />
                Réponse rapide sous 24h – étude gratuite et sans engagement
              </p>

              <div
                data-testid="hero-social-proof"
                className="mt-6 inline-flex items-center gap-3 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-full"
              >
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={14} weight="fill" color="#F59E0B" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-slate-800">
                  +250 foyers accompagnés en 2025
                </span>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4 max-w-lg">
                {[
                  "Étude personnalisée et rapide",
                  "Accompagnement complet",
                  "Professionnels qualifiés",
                  "Solutions adaptées",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2">
                    <CheckCircle size={18} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs text-slate-500">
                Aides disponibles selon votre situation. Reste à charge variable selon profil.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(30,64,175,0.18)]">
                <img
                  src={IMAGES.hero}
                  alt="Maison moderne avec pompe à chaleur"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
              </div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="absolute -bottom-5 -left-3 md:-left-8 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 max-w-[260px]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CurrencyEur size={20} weight="bold" color="#059669" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Factures chauffage</div>
                    <div className="font-bold text-slate-900 text-sm">
                      Réductions possibles
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -top-4 right-3 md:-right-4 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 max-w-[240px]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
                    <ShieldCheck size={20} weight="fill" color="#1E40AF" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Sans engagement</div>
                    <div className="font-bold text-slate-900 text-sm">
                      Étude personnalisée
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AIDES DE L'ETAT */}
      <Aides />

      {/* AVANTAGES */}
      <section data-testid="advantages-section" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-3">
              Pourquoi Hélios Solutions
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              Un accompagnement clair, humain et sans engagement
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {ADVANTAGES.map((a, i) => {
              const Icon = iconMap[a.icon] || Leaf;
              return (
                <motion.div
                  key={a.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  data-testid={`advantage-${i}`}
                  className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-[0_12px_30px_rgba(30,64,175,0.08)] transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center mb-4">
                    <Icon size={22} weight="fill" color="#FFFFFF" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-1.5">
                    {a.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMENT CA MARCHE */}
      <section
        data-testid="how-section"
        className="py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-blue-50/30"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">
              Comment ça marche
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              Un parcours simple, en 3 étapes
            </h2>
            <p className="text-slate-600 mt-3">
              Un processus transparent pour avancer sereinement sur votre projet.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.slice(0, 3).map((s, i) => (
              <motion.div
                key={s.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`step-${i}`}
                className="relative p-7 rounded-2xl bg-white border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
              >
                <div className="text-5xl font-heading font-bold text-blue-700/10 absolute top-4 right-5">
                  {s.number}
                </div>
                <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold font-heading text-lg mb-4">
                  {i + 1}
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/comment-ca-marche"
              data-testid="how-link"
              className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-800"
            >
              Voir le parcours complet en 4 étapes <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS FAIRE CONFIANCE */}
      <section data-testid="trust-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src={IMAGES.renovation}
                alt="Travaux de rénovation énergétique"
                className="w-full h-[420px] object-cover rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-5 -right-3 md:-right-6 bg-white p-5 rounded-2xl shadow-xl border border-slate-100 max-w-[240px]">
                <div className="flex items-center gap-2 mb-2">
                  <Certificate size={20} weight="fill" color="#1E40AF" />
                  <span className="font-heading font-bold text-slate-900">Qualifiés</span>
                </div>
                <p className="text-xs text-slate-600">
                  Artisans sélectionnés pour leur sérieux et leur savoir-faire.
                </p>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-3">
                Pourquoi nous faire confiance
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-5">
                Un interlocuteur dédié, des solutions adaptées
              </h2>
              <div
                data-testid="trust-social-proof"
                className="inline-flex items-center gap-3 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-full mb-6"
              >
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={14} weight="fill" color="#F59E0B" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-slate-800">
                  +250 foyers accompagnés en 2025
                </span>
              </div>
              <div className="space-y-4">
                {[
                  {
                    icon: ShieldCheck,
                    title: "Transparence sur les aides et le reste à charge",
                    desc: "Nous présentons les dispositifs potentiellement mobilisables selon votre situation, sans fausse promesse.",
                  },
                  {
                    icon: HeadCircuit,
                    title: "Accompagnement complet, du premier échange à la mise en service",
                    desc: "Un interlocuteur dédié vous suit à chaque étape du projet.",
                  },
                  {
                    icon: House,
                    title: "Des solutions dimensionnées pour votre logement",
                    desc: "Pas d'offre standard : chaque étude est personnalisée selon votre situation.",
                  },
                  {
                    icon: Clock,
                    title: "Réactivité et sérieux",
                    desc: "Nous vous rappelons rapidement et respectons nos engagements.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4" data-testid={`trust-item-${i}`}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex-shrink-0 flex items-center justify-center">
                      <item.icon size={20} weight="fill" color="#059669" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-sm text-slate-600 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEMOIGNAGES */}
      <Testimonials />

      {/* REALISATIONS */}
      <Realisations />

      {/* FAQ */}
      <FAQ />

      {/* CTA RAPPEL */}
      <section
        data-testid="callback-cta-section"
        className="relative py-20 lg:py-28 bg-gradient-to-br from-blue-700 to-blue-900 overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #10B981 0%, transparent 50%), radial-gradient(circle at 80% 80%, #ffffff 0%, transparent 50%)"
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
                <ChatCircleDots size={14} weight="fill" /> Être rappelé rapidement
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Prêt à réduire vos factures de chauffage ?
              </h2>
              <p className="text-blue-100 mt-4 text-base md:text-lg max-w-xl">
                Un expert Hélios Solutions vous rappelle rapidement pour étudier
                votre projet et vous présenter les solutions adaptées.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={COMPANY.phoneHref}
                  data-testid="cta-call-btn"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-semibold bg-white text-blue-800 hover:bg-slate-100 transition-colors"
                >
                  <Phone size={18} weight="fill" /> {COMPANY.phone}
                </a>
                <Link
                  to="/eligibilite"
                  data-testid="cta-eligibility-btn"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                >
                  Tester mon éligibilité gratuitement <Sparkle size={16} weight="fill" />
                </Link>
              </div>
              <p className="text-sm text-blue-100 mt-4 font-medium flex items-center gap-2">
                <Clock size={14} weight="fill" className="text-emerald-300" />
                Réponse rapide sous 24h – étude gratuite et sans engagement
              </p>
              <p className="text-xs text-blue-200 mt-3">
                Étude personnalisée et sans engagement. Aides disponibles selon votre situation.
              </p>
            </div>
            <div>
              <CallbackForm testIdPrefix="home-callback" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
